import { Injectable, ComponentRef, Injector, ApplicationRef, ComponentFactoryResolver, Type, InjectionToken } from '@angular/core';
import { normalizeHexColor } from './utils.service';

export const DIALOG_DATA = new InjectionToken<any>('DIALOG_DATA');

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private dialogComponentRef: ComponentRef<any> | null = null;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  open<T>(component: Type<T>, data: any = {}): ComponentRef<T> {
    if (this.dialogComponentRef) {
      this.closeDialog();
    }

    const themeVariables = data.customThemeVariables || this.getComputedThemeVariables();

    const dialogInjector = Injector.create({
      providers: [
        { provide: DIALOG_DATA, useValue: { ...data, theme: themeVariables } }
      ],
      parent: this.injector
    });

    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(component)
      .create(dialogInjector);

    this.appRef.attachView(componentRef.hostView);

    const domElem = componentRef.location.nativeElement as HTMLElement;
    this.applyThemeVariables(domElem, themeVariables);
    document.body.appendChild(domElem);

    this.dialogComponentRef = componentRef;

    return componentRef;
  }

  closeDialog() {
    if (this.dialogComponentRef) {
      this.appRef.detachView(this.dialogComponentRef.hostView);
      this.dialogComponentRef.destroy();
      this.dialogComponentRef = null;
    }
  }

  private getComputedThemeVariables(): Record<string, string> {
    const computedStyle = getComputedStyle(document.documentElement);
    const themeVariables: Record<string, string> = {};

    [
      '--ufp-primary-color',
      '--ufp-background-color',
      '--ufp-text-color',
      '--ufp-text-muted',
      '--ufp-border-color',
      '--ufp-input-background',
    ].forEach((cssVariable) => {
      const value = computedStyle.getPropertyValue(cssVariable).trim();
      if (value) {
        themeVariables[cssVariable] = value;
      }
    });

    return themeVariables;
  }

  private applyThemeVariables(element: HTMLElement, variables: Record<string, string>) {
    Object.entries(variables).forEach(([key, value]) => {
      const normalizedValue = key.includes('color')
        ? normalizeHexColor(value) || value
        : value;
      element.style.setProperty(key, normalizedValue);
    });
  }
}
